import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { ExternalLink, Linkedin, MapPin, Search, Sparkles } from "lucide-react";

export default function Enterprises() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [sector, setSector] = useState("all");

    useEffect(() => {
        api.get("/enterprises")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    const sectors = useMemo(() => {
        const s = new Set(items.map((i) => i.sector).filter(Boolean));
        return ["all", ...Array.from(s)];
    }, [items]);

    const filtered = useMemo(() => {
        return items.filter((i) => {
            const matchesSector = sector === "all" || i.sector === sector;
            const text = `${i.startup_name} ${i.founder_name} ${i.description}`.toLowerCase();
            const matchesQ = !q || text.includes(q.toLowerCase());
            return matchesSector && matchesQ;
        });
    }, [items, q, sector]);

    return (
        <main data-testid="enterprises-page" className="text-white pt-32 pb-24">
            <SEO
                title="Directory | Purposeful, Inc."
                description="A curated directory of impact-driven founders and ventures. 100 founders across 12+ sectors, one shared question: what does it take to build a business that matters?"
                path="/directory"
                keywords="founder directory, impact enterprises directory, purposeful directory, businesses that matter, impact founders India"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    "@id": "https://purposeful.inc/directory#page",
                    "url": "https://purposeful.inc/directory",
                    "name": "Directory — Purposeful, Inc.",
                    "description": "Directory of founders and ventures featured on Purposeful, Inc.",
                    "isPartOf": { "@id": "https://purposeful.inc/#website" },
                    "mainEntity": {
                        "@type": "ItemList",
                        "numberOfItems": items.length,
                        "itemListElement": items.slice(0, 25).map((e, i) => ({
                            "@type": "ListItem",
                            "position": i + 1,
                            "item": {
                                "@type": "Organization",
                                "name": e.startup_name,
                                "url": e.website || undefined,
                                "description": e.description,
                                "founder": { "@type": "Person", "name": e.founder_name }
                            }
                        }))
                    }
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Directory", path: "/directory" }]}
            />

            <section className="container-x mx-auto max-w-7xl">
                <div className="overline mb-4">Directory</div>
                <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                    A living directory of<br />
                    <span className="text-[#D4AF37]">founders and ventures.</span>
                </h1>
                <p className="mt-5 text-zinc-400 text-lg max-w-2xl">
                    Curated founders and impact-driven ventures within the
                    Purposeful ecosystem. Filter by sector, browse by name.
                </p>
                <div className="mt-8">
                    <Link
                        to="/contact?topic=Get%20Featured"
                        data-testid="featured-get-featured-cta"
                        className="btn-gold"
                    >
                        <Sparkles size={16} /> Get listed
                    </Link>
                </div>

                {/* Filters */}
                <div className="mt-12 flex flex-col md:flex-row gap-4 md:items-center">
                    <div className="relative flex-1 max-w-md">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                        <input
                            type="text"
                            placeholder="Search startups, founders, sectors…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="enterprise-search-input"
                            className="w-full rounded-full bg-white/5 border border-white/10 pl-11 pr-5 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37] transition"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {sectors.map((s) => (
                            <button
                                key={s}
                                onClick={() => setSector(s)}
                                data-testid={`enterprise-sector-${s}`}
                                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition ${
                                    sector === s
                                        ? "bg-[#D4AF37] text-black"
                                        : "border border-white/15 text-zinc-300 hover:border-[#D4AF37]"
                                }`}
                            >
                                {s === "all" ? "All sectors" : s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid */}
                <div className="mt-12">
                    {loading ? (
                        <p className="text-zinc-500">Loading…</p>
                    ) : filtered.length === 0 ? (
                        <div className="card-dark p-12 text-center" data-testid="enterprise-empty">
                            <p className="text-zinc-400">The directory is being curated. New founders and ventures will appear here soon.</p>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filtered.map((e) => (
                                <article key={e.id} data-testid="directory-card" className="card-dark p-8 flex flex-col">
                                    <div className="flex items-start justify-between gap-4">
                                        {e.logo_url ? (
                                            <img src={e.logo_url} alt={e.startup_name} className="h-12 w-12 rounded-lg object-cover ring-1 ring-white/10" />
                                        ) : (
                                            <div className="h-12 w-12 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center font-display text-[#D4AF37] text-lg">
                                                {e.startup_name?.[0]}
                                            </div>
                                        )}
                                        <span className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-3 py-1">{e.sector}</span>
                                    </div>
                                    <h3 className="font-display text-2xl mt-5">{e.startup_name}</h3>
                                    <p className="text-zinc-400 text-sm mt-1">By {e.founder_name}</p>
                                    {e.location && (
                                        <p className="text-zinc-500 text-xs mt-1 flex items-center gap-1"><MapPin size={12} /> {e.location}</p>
                                    )}
                                    <p className="text-zinc-300 text-sm mt-4 leading-relaxed line-clamp-4">{e.description}</p>
                                    {e.impact_statement && (
                                        <p className="text-[#D4AF37]/90 text-xs mt-4 italic">"{e.impact_statement}"</p>
                                    )}
                                    <div className="mt-6 flex gap-3 text-xs">
                                        {e.website && (
                                            <a href={e.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-zinc-300 hover:text-[#D4AF37]" data-testid="enterprise-website-link">
                                                <ExternalLink size={12} /> Website
                                            </a>
                                        )}
                                        {e.founder_linkedin && (
                                            <a href={e.founder_linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-zinc-300 hover:text-[#D4AF37]" data-testid="enterprise-linkedin-link">
                                                <Linkedin size={12} /> Founder
                                            </a>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
}
